import { forwardRef } from 'react';
import type { ComponentType, SVGProps } from 'react';

import { IconButton } from 'components/Button';
import { cn } from 'components/utils';

import {
  alertContentRecipe,
  alertDescriptionRecipe,
  alertDismissRecipe,
  alertIconRecipe,
  alertRecipe,
  alertTitleRecipe,
} from './Alert.recipe';
import CheckCircleIcon from './check-circle.svg?react';
import CloseIcon from './close.svg?react';
import InfoIcon from './info.svg?react';
import WarningCircleIcon from './warning-circle.svg?react';
import WarningIcon from './warning.svg?react';

import type { AlertProps, AlertRole, AlertSeverity } from './types';

/**
 * The severity glyphs, committed as the file's own 20px exports rather than taken from `icons/`.
 *
 * Three reasons, in order of weight:
 *
 *   - `icons/` renders through MUI's `SvgIcon` and reads the visual-refresh flag via
 *     `useInheritedVisualRefresh`, so an alert built on it would need an MUI theme provider in
 *     scope — which is exactly what HNS-339 says a Design System 2.0 component must not need.
 *   - with the flag off those exports resolve to the LEGACY glyph, so a refreshed alert would draw
 *     Material icons.
 *   - the exports are what the file specifies, at the size the file specifies it. The library's icon
 *     components are 16px frames carrying a ~13px glyph at a 1.2px weight, and the alert places each
 *     one in a 20px box — so the glyph renders at ~16.5px on a ~1.5px stroke. That is exactly what
 *     these 20px exports draw. A direct Phosphor import at 20px would be a near-miss.
 *
 * `currentColor` throughout, which is now the whole colour story: the reworked set draws all five
 * glyphs in `foreground/primary`, the same colour as the text, so there is no icon colour slot to
 * feed. HNS-264 retires the flag and with it the first two reasons above.
 *
 * SUPERSEDED IN PART, and worth reading before touching these files. `components/Icon` (HNS-262,
 * landed 2026-09-16) is a provider-free DS 2.0 icon library carrying all five of these glyphs as
 * `InfoIcon`, `CheckCircleIcon`, `WarningIcon`, `WarningCircleIcon` and `XIcon` — so the first two
 * reasons above no longer argue for local copies, only against the MUI `icons/` barrel. The reason
 * this component has not moved yet is that the two are not the same DRAWING: the library emits
 * Phosphor's 256-unit filled path plus a 3.2-unit stroke, while these are 20-unit open strokes at
 * 1.5. Figma's own icon components are filled paths with no stroke, so the library is the closer
 * construction of the two and the migration is probably right — but it is a visual change, the
 * Alert would be the library's FIRST consumer, and `Tag` carries a duplicate `close.svg` that
 * should move in the same pass. Tracked as a follow-up rather than folded into a parity port.
 *
 * Neutral and Info share `Info`. That is the design, not a shortcut — the two cells now differ only
 * in the PANEL's colour (nodes 639:77520 and 639:77191), which is what makes Neutral the severity
 * whose glyph carries no severity.
 */
const ICONS: Record<AlertSeverity, ComponentType<SVGProps<SVGSVGElement>>> = {
  neutral: InfoIcon,
  info: InfoIcon,
  success: CheckCircleIcon,
  warning: WarningIcon,
  error: WarningCircleIcon,
};

/**
 * Politeness by severity.
 *
 * The split is at warning: a warning and an error are things the user needs to hear now, and the
 * other three can wait for a natural pause. This is the component's answer to "an informational
 * notice must not interrupt", and it is a default rather than a rule — `role` overrides it.
 *
 * Worth knowing when reading a call site: a live region only announces content that CHANGES while
 * it is mounted. An alert rendered as part of the initial page is read in document order like any
 * other text, whichever role it carries; the role matters for one that appears in response to
 * something the user did.
 */
const ROLES: Record<AlertSeverity, AlertRole> = {
  neutral: 'status',
  info: 'status',
  success: 'status',
  warning: 'alert',
  error: 'alert',
};

/**
 * Design System 2.0 alert.
 *
 * Ported from the Figma component set (file 5aSkE0U4TeWbuIZDvccHjc, node 639:77378) and re-read
 * after its September 2026 rework. Props mirror the set's properties, so "Type=Warning,
 * isDismissible" reads straight across: `severity`, `onDismiss`.
 *
 * Theme is not a prop, and the set no longer has a Mode axis either. Colours resolve from the
 * nearest `.dark` ancestor through the semantic token layer in `components/styles/design-system.css`,
 * so the same markup is correct in both themes.
 *
 * A static, in-page message about the surface the user is on — distinct from a toast, which is
 * transient and comes through the Redux `alerts` module, and from the legacy `common/components`
 * alert, banner and ribbon, all three of which wrap MUI and stay where they are.
 *
 * Severity is carried three ways, which is the point of the component: the panel's colour, the
 * glyph's SHAPE, and the politeness with which it is announced. None of the three is load-bearing
 * alone, so a user who cannot distinguish the colours still gets the message. (The glyph itself no
 * longer carries a severity colour — the rework made all five `foreground/primary`.)
 *
 * Two axes of today's legacy alert are deliberately absent because the refreshed set does not draw
 * them: there is no actions row and no self-hiding. Both are open questions on HNS-339.
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      severity,
      children,
      title,
      role,
      onDismiss,
      dismissLabel,
      className,
      titleClassName,
      descriptionClassName,
      ...rest
    },
    ref
  ) => {
    const Icon = ICONS[severity];
    const resolvedRole = role ?? ROLES[severity];

    /**
     * Narrowed as a pair, because `onDismiss` and `dismissLabel` were destructured out of a
     * discriminated union and TypeScript cannot relate two separate locals back to it. `AlertProps`
     * already makes one without the other unconstructable, so the `&&` is a formality that lets
     * `IconButton`'s required `aria-label: string` be satisfied without an assertion.
     */
    const dismiss = onDismiss && dismissLabel ? { onDismiss, dismissLabel } : null;

    return (
      <div
        ref={ref}
        // Derived from the resolved role rather than fixed, and set explicitly rather than left to
        // the role's implied politeness, because not every assistive technology maps one to the
        // other. Both sit before `rest` so a call site can still override either.
        aria-live={resolvedRole === 'alert' ? 'assertive' : 'polite'}
        className={cn(alertRecipe({ severity }), className)}
        data-part="panel"
        data-severity={severity}
        role={resolvedRole}
        {...rest}
      >
        {/* `aria-hidden` because the shape is a visual restatement of the severity, which the role
            and the wording already carry. Named to a screen reader it would just be noise. */}
        <span className={alertIconRecipe()} data-part="icon">
          <Icon aria-hidden="true" focusable="false" />
        </span>

        <div className={alertContentRecipe()}>
          {title !== undefined && (
            <p className={cn(alertTitleRecipe(), titleClassName)} data-part="title">
              {title}
            </p>
          )}

          <p className={cn(alertDescriptionRecipe(), descriptionClassName)} data-part="description">
            {children}
          </p>
        </div>

        {dismiss && (
          /* Out of the flow and pinned to the panel's top-right, which is what the set does — so the
             panel's height and the text column's width are the same whether or not the alert is
             dismissible. See `alertDismissRecipe` for the offsets, the 16px glyph, and what
             `muted`/`default` adds on a coloured panel. */
          <IconButton
            appearance="muted"
            aria-label={dismiss.dismissLabel}
            className={alertDismissRecipe()}
            data-part="dismiss"
            fill="default"
            onClick={dismiss.onDismiss}
            size="2xs"
          >
            <CloseIcon aria-hidden="true" focusable="false" />
          </IconButton>
        )}
      </div>
    );
  }
);
